// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { MediaItem, MediaFile } from '../types';
import { EditMediaComponent } from './EditMediaComponent';
import { ProductFormComponent } from './ProductFormComponent';
import { doc, updateDoc, getFirestore, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

interface EditorModalProps {
    item: MediaItem | null;
    isOpen: boolean;
    onClose: () => void;
    onItemUpdate?: (updatedItem: MediaItem) => void;
    shopId: string;
}

export const EditorModal: React.FC<EditorModalProps> = ({ item, isOpen, onClose, onItemUpdate, shopId }) => {
    const { currentUser } = useAuth();
    const [currentItem, setCurrentItem] = useState<MediaItem | null>(item);
    const [loading, setLoading] = useState(false);

    // Update currentItem when item prop changes
    useEffect(() => {
        setCurrentItem(item);
    }, [item]);

    // Early return after all hooks
    if (!isOpen || !item) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleMediaUpdate = (updatedMedia: MediaFile[]) => {
        if (currentItem) {
            const updatedItem = { ...currentItem, media: updatedMedia };
            setCurrentItem(updatedItem);
        }
    };

    const handleProductUpdate = (updatedItem: MediaItem) => {
        setCurrentItem(updatedItem);
    };

    const saveAllChanges = async () => {
        if (!currentUser || !currentItem || !shopId) return;

        setLoading(true);
        try {
            const db = getFirestore();
            const updateData: any = {};

            // Include all current item data
            if (currentItem.name !== item?.name) {
                updateData.name = currentItem.name;
            }
            if (currentItem.description !== item?.description) {
                updateData.description = currentItem.description;
            }
            if (currentItem.price?.cost !== item?.price?.cost) {
                updateData.price = currentItem.price;
            }
            if (currentItem.features !== item?.features) {
                updateData.features = currentItem.features;
            }
            if (currentItem.isPublished !== item?.isPublished) {
                updateData.isPublished = currentItem.isPublished;
            }
            if (JSON.stringify(currentItem.variants) !== JSON.stringify(item?.variants)) {
                updateData.variants = currentItem.variants;
            }
            // Always update media array to capture changes
            if (JSON.stringify(currentItem.media) !== JSON.stringify(item?.media)) {
                updateData.media = currentItem.media;
            }

            if (Object.keys(updateData).length > 0) {
                await updateDoc(doc(db, 'shops', shopId, 'products', currentItem.id), updateData);

                // Notify parent component of the update
                if (onItemUpdate) {
                    onItemUpdate(currentItem);
                }
            }

            onClose();
        } catch (error) {
            console.error('Error saving changes:', error);
            alert('Failed to save changes');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={handleBackdropClick}
        >
            <div className="relative w-full h-full max-w-4xl overflow-y-auto">
                <div className="absolute top-4 right-4 flex gap-2 z-50">
                    <button
                        onClick={() => window.open(`https://looksbysellox.com/shops/sellox/product/${currentItem?.$key}`, '_blank')}
                        className="text-white bg-blue-600 hover:bg-blue-700 rounded-full p-2"
                        title="Open product page"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                    </button>

                    <button
                        onClick={onClose}
                        className="text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2"
                        title="Close editor"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Media Section - Full Screen Background */}
                <div className="inset-0 bg-black">
                    {currentItem && (
                        <EditMediaComponent
                            item={currentItem}
                            onMediaUpdate={handleMediaUpdate}
                        />
                    )}
                </div>

                {/* Scrollable Content Section - Overlaying */}
                <div className="bg-white rounded-t-3xl shadow-2xl z-30 flex flex-col">
                    <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-4 flex-shrink-0"></div>
                    <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', minHeight: '0' }}>
                        {currentItem && (
                            <ProductFormComponent
                                item={currentItem}
                                onProductUpdate={handleProductUpdate}
                            />
                        )}
                    </div>
                    <button
                        onClick={saveAllChanges}
                        disabled={loading}
                        className="text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-full p-2"
                        title="Save changes"
                    >
                        Save {loading ? (
                            <svg className="w-6 h-6 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        ) : (
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditorModal;
